import type { FetchTrackedOneGraphOperation } from "./ApolloNativeModule";
import type { FragmentRegistryAPI } from "@apollo/client/cache";

import { createHttpHeaderLink, typePolicies as defaultTypePolicies } from "./apolloClientShared";
import { createGraphQLOperationLink } from "./createGraphQLOperationLink";
import { isSSR } from "../util/isSSR";

import {
  ApolloClient,
  type ApolloCache,
  type ApolloLink,
  type DefaultOptions,
  from,
  HttpLink,
  InMemoryCache,
  type NormalizedCacheObject,
  type TypePolicies,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";

const ERROR_POLICY_DEFAULTS: DefaultOptions = {
  watchQuery: { errorPolicy: "all" },
  query: { errorPolicy: "all" },
  mutate: { errorPolicy: "all" },
};

function mergeDefaultOptions(overrides?: DefaultOptions): DefaultOptions {
  if (!overrides) return ERROR_POLICY_DEFAULTS;
  return {
    watchQuery: { ...ERROR_POLICY_DEFAULTS.watchQuery, ...overrides.watchQuery },
    query: { ...ERROR_POLICY_DEFAULTS.query, ...overrides.query },
    mutate: { ...ERROR_POLICY_DEFAULTS.mutate, ...overrides.mutate },
  };
}

interface CreateOneGraphApolloClientOptions {
  fetch?: FetchTrackedOneGraphOperation;
  trackingKey?: string;
  defaultOptions?: DefaultOptions;
  cache?: ApolloCache<NormalizedCacheObject>;
  platformOptions?: {
    web?: {
      graphqlUrl?: string;
      graphqlApiKey?: string;
    };
  };
  typePolicies?: TypePolicies;
  errorLink?: ApolloLink;
  prforceGroups?: string;
  fragments?: FragmentRegistryAPI;
  placementId?: string;
  country?: string;
  /** Host-provided CTK, sent as the `indeed-ctk` auth header. */
  ctk?: string;
  cookieHeader?: string;
  /**
   * When true (default), the browser client rehydrates from `window.__APOLLO_STATE__`.
   * Pass false on the servlet REST path so secondary queries do not seed from
   * the host page's SSR cache.
   */
  restoreWindowApolloState?: boolean;
}

export function createOneGraphApolloClient({
  trackingKey,
  defaultOptions,
  cache,
  platformOptions,
  typePolicies,
  errorLink,
  prforceGroups,
  fragments,
  placementId,
  country,
  ctk,
  cookieHeader,
  restoreWindowApolloState = true,
}: CreateOneGraphApolloClientOptions): ApolloClient<NormalizedCacheObject> {
  const defaultAuthLink = setContext(({}, { headers }) => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      headers: {
        ...headers,
        ...(isSSR()
          ? {
              "Indeed-Api-Key": platformOptions?.web?.graphqlApiKey,
              "indeed-co": country,
              Cookie: cookieHeader,
            }
          : {
              "indeed-api-key": platformOptions?.web?.graphqlApiKey,
              ...(country ? { "indeed-co": country } : {}),
            }),
        ...(ctk ? { "indeed-ctk": ctk } : {}),
      },
    };
  });

  // SSR cannot batch (no event loop tick to coalesce on) and must rehydrate
  // nothing; the browser keeps the existing BatchHttpLink behavior.
  const httpLink = isSSR()
    ? new HttpLink({ uri: platformOptions?.web?.graphqlUrl })
    : new BatchHttpLink({
        credentials: "include",
        uri: platformOptions?.web?.graphqlUrl,
        batchMax: 5,
        batchInterval: 5,
      });

  // On the browser, rehydrate from the cache the server serialized into the
  // page (window.__APOLLO_STATE__) so SSR'd queries don't re-run as loading.
  const resolvedCache =
    cache ??
    (() => {
      const inMemoryCache = new InMemoryCache({
        typePolicies: Object.assign({}, defaultTypePolicies, typePolicies),
        fragments,
      });
      if (!isSSR() && restoreWindowApolloState) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        const serializedState = (window as unknown as { __APOLLO_STATE__?: NormalizedCacheObject })
          .__APOLLO_STATE__;
        if (serializedState) {
          inMemoryCache.restore(serializedState);
        }
      }
      return inMemoryCache;
    })();

  return new ApolloClient({
    ssrMode: isSSR(),
    cache: resolvedCache,
    defaultOptions: mergeDefaultOptions(defaultOptions),
    link: from([
      createHttpHeaderLink([
        ...(trackingKey ? [{ name: "indeed-root-tracking-key", value: trackingKey }] : []),
        ...(prforceGroups ? [{ name: "indeed-prforce-groups", value: prforceGroups }] : []),
        // SSR-only: `indeed-placementid` is absent from the OneGraph router's CORS
        // `allow_headers` (router/config/base.yaml), so a browser preflight rejects any CSR
        // request carrying it. SSR runs server-to-server, where CORS does not apply.
        // ONEGRAPH-7574 proposes adding the header router-side; once that ships, delete this
        // `isSSR()` guard and send the header on CSR as well.
        ...(placementId && isSSR() ? [{ name: "indeed-placementid", value: placementId }] : []),
      ]),
      ...(errorLink ? [errorLink] : []),
      // Browser-only: an SSR render has no user session to attribute an operation to, and
      // counting server-side queries would double-count against every client-side operation.
      ...(isSSR() ? [] : [createGraphQLOperationLink()]),
      defaultAuthLink.concat(httpLink),
    ]),
  });
}
